import { useEffect, useState } from "react"

import { App, Button, Flex, Layout, Modal, Space, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import ReactPlayer from "react-player"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button/index.js"
import EnrollmentCard from "../../../Components/Enrollment/EnrollmentCard.js"
import Icon from "../../../Icon/index.js"
import { modalConfig } from "../../../theme.js"
import { cdrnModalList, createAccountStepPath, onboardingStepPath } from "../../../Utils/utils.js"
import { BA_STEPS } from "../../GettingStartedPage/utils/businessSteps.js"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps.js"
import { updateProfile } from "../../LoginPage/redux/auth.actions"
import { changeNextCreateAccountStep } from "../../User/redux/user.actions"
import { userActions } from "../../User/redux/user.slice"
import {
  changeOnboardingStep,
  getEnrollments,
  updateEnrollment,
  userSave
} from "../../User/redux/user.thunks"
import { updateCompletedStep } from "../../User/services.js"
import MobileCtaButtons from "../PaymentConnectPage/components/MobileCtaButtons/MobileCtaButtons.js"

import "./styles.less"

const { Title, Text } = Typography
const OnboardingEnrollmentPage = (props) => {
  const { step } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()
  const { fetching, userInfo } = useSelector((state) => state.auth)
  const { isMobile, theme } = useSelector((state) => state.nav)
  const onboardingFetching = useSelector((state) => state.user.fetching)
  const enrollments = useSelector((state) => state.user.enrollments)
  const saveFetching = useSelector((state) => state.user.saveFetching)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const saveResult = useSelector((state) => state.user.saveResult)
  const nextCreateAccountStep = useSelector((state) => state.user.nextCreateAccountStep)

  const currentBusinessAccountId = userInfo?.["current_business_account_id"]
  const businessId =
    businessAccount?.id === currentBusinessAccountId
      ? businessAccount?.id
      : currentBusinessAccountId
  const isActiveEnrollments = enrollments.some(({ is_active }) => is_active)
  const isNewBusiness = location.pathname?.includes("/create-account")
  //show modal
  const [visible, setVisible] = useState(false)
  const [selectEnrollment, setSelectEnrollment] = useState(null)

  useEffect(() => {
    if (!userInfo?.["new_onboarding_finished"] && !isActiveEnrollments) {
      if (userInfo?.next_step - 2 > step) {
        dispatch(changeOnboardingStep({ next_step: step + 2 }))

        updateCompletedStep({
          step: step - 1,
          business_account_id: businessId
        })
      }
    }
  }, [isActiveEnrollments])

  const handleNavigate = () => {
    if (isNewBusiness) {
      navigate(createAccountStepPath[7])
      return
    }
    navigate(onboardingStepPath[2])
  }

  function handleSubmit() {
    if (isActiveEnrollments) {
      if (nextCreateAccountStep) {
        updateCompletedStep({ step: 3, business_account_id: businessId })
        dispatch(changeNextCreateAccountStep(4))
        if (userInfo?.is_created_by_invitation) {
          dispatch(updateProfile({ next_step: 6 }))
          dispatch(userSave({ next_step: 6 }))
        }
        navigate(createAccountStepPath[4])
      } else {
        if (userInfo?.next_step - 2 === step) dispatch(changeOnboardingStep({ next_step: 6 }))

        updateCompletedStep({ step: step, business_account_id: businessId })
        navigate(onboardingStepPath[step + 2 + 1])
      }
    } else {
      modal.warning({
        ...modalConfig("info", 442),
        className: "ob-clear-bg cdrn",
        okButtonProps: { size: "large", style: { width: "auto" } },
        centered: !isMobile,
        wrapClassName: isMobile ? "ob-modal-bottom" : "",

        title: t("Connect at least one enrollment"),
        content: t(
          "To activate your Chargeback account, simply enable at least one enrollment. With pay-as-you-go, you're only charged for prevented chargebacks."
        ),
        okText: t("Confirm")
      })
    }
  }

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" },
    placement: isMobile ? "topRight" : undefined
  }

  useEffect(() => {
    if (userInfo && businessId) dispatch(getEnrollments(businessId))
    document.title = "Enrollment - Chargeback"

    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [businessId, businessAccount])

  useEffect(() => {
    if (saveResult) {
      if (saveResult?.enrollment_turn_on) {
        notification.success({
          message: t("Enrollment turned on"),
          description: t("_name_ enrollment was turned on successfully.", {
            name: saveResult?.enrollment_turn_on
          }),
          ...notificationConfig
        })
      }
      if (saveResult?.enrollment_turn_off) {
        notification.success({
          message: t("Enrollment turned off"),
          description: t("_name_ enrollment was turned off successfully.", {
            name: saveResult?.enrollment_turn_off
          }),
          ...notificationConfig
        })
      }
      setSelectEnrollment(null)
      dispatch(userActions.userSaveResultReset())
    }
  }, [saveResult])

  const handleChangeEnrollment = (enrollment) => {
    dispatch(
      updateEnrollment({
        id: enrollment?.id,
        is_active: !enrollment?.["is_active"]
      })
    )
  }

  const connectCDRN = (enrollment) => {
    modal.confirm({
      ...modalConfig("exclamation", 442),
      className: "ob-clear-bg cdrn",
      centered: !isMobile,
      wrapClassName: isMobile ? "ob-modal-bottom" : "",
      title: t("Please confirm enrollment in both Visa products"),
      content: (
        <>
          <div style={{ paddingBottom: 16 }}>
            <Text type="secondary">
              {t("Enabling both ")}
              <Text>{t("RDR")}</Text> {t(" and ")}
              <Text>{t("CDRN")}</Text>{" "}
              {t(
                " does not provide more protection. It only reduces RDR alerts and adds cases that sometimes need manual review."
              )}
            </Text>
          </div>
          <div style={{ paddingBottom: 16 }}>
            <Text type="secondary">
              {t("For full coverage and automation, ")}
              <Text>{t("we recommend using Visa RDR only.")}</Text>{" "}
              {t(
                " CDRN is best suited for merchants selling in the US that wants more manual intervention."
              )}
            </Text>
          </div>
          <ul>
            {cdrnModalList.map((item, index) => (
              <li key={index}>
                <Text type="secondary">{item}</Text>
              </li>
            ))}
          </ul>
          <br />
        </>
      ),

      okText: t("Continue anyway"),
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        handleChangeEnrollment(enrollment)
      }
    })
  }

  const disconnectPayment = (enrollment) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      className: "ob-clear-bg cdrn",
      centered: !isMobile,
      wrapClassName: isMobile ? "ob-modal-bottom" : "",

      title: t("Turn off enrollment"),
      content: t(
        "Are you sure you want to turn off this enrollment? More enrollments enable higher chargeback prevention rates."
      ),

      cancelText: t("Cancel"),
      okText: t("Turn off"),
      cancelButtonProps: { width: 70, height: 40 },
      okButtonProps: {
        width: 75,
        height: 40
      },
      onOk() {
        handleChangeEnrollment(enrollment)
      }
    })
  }

  const handleBack = () => {
    if (isNewBusiness) {
      return navigate(createAccountStepPath[BA_STEPS.INTEGRATION])
    }
    navigate(onboardingStepPath[OB_STEPS.INTEGRATION])
  }

  return (
    <Layout>
      <Spin size="large" spinning={fetching || onboardingFetching || saveFetching}>
        <Flex
          vertical
          align={"center"}
          style={{ margin: "0 0 auto", width: "100%", maxWidth: 1074 }}
        >
          <Flex
            gap={16}
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
            wrap="wrap"
          >
            <Space direction="vertical" size={0}>
              {!isMobile && (
                <Button
                  className="back-btn"
                  type="text"
                  icon={<Icon type="arrow_left" />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              )}
              <h2
                className="enrollment_header"
                style={{
                  textAlign: "left",
                  marginBottom: 8,
                  fontSize: isMobile && 18,
                  lineHeight: isMobile && "24px"
                }}
              >
                {t("Choose enrollment")}
              </h2>
              <div style={{ marginBottom: 20 }}>
                <Text className="enrollment_subtitle" type={"secondary"}>
                  {t(
                    "You will receive only one alert type per chargeback. You will also only pay when we help you prevent a chargeback."
                  )}
                </Text>
              </div>

              <Text className="enrollment_subtitle" type={"secondary"} style={{ width: 320 }}>
                {t(
                  "If you are unsure, keep our recommendations on for the best prevention and smooth operations."
                )}
              </Text>
            </Space>
          </Flex>

          <Flex
            className="myScrollRow "
            gap={16}
            style={{ margin: "24px 0px 32px 0px" }}
            // justify="center"
            // align="flex-end"
            justify="flex-start"
            align="flex-end"
          >
            {enrollments.map((enrollment) => (
              <EnrollmentCard
                key={enrollment?.id}
                enrollment={enrollment}
                isOnboarding
                style={{ width: !isMobile && 306 }}
                theme={theme}
              >
                <ButtonComponent
                  size="large"
                  danger={enrollment["is_active"]}
                  loading={saveFetching && enrollment?.id === selectEnrollment}
                  onClick={() => {
                    setSelectEnrollment(enrollment?.id)
                    enrollment["is_active"]
                      ? disconnectPayment(enrollment)
                      : enrollment.name.toLowerCase().includes("cdrn")
                        ? connectCDRN(enrollment)
                        : handleChangeEnrollment(enrollment)
                  }}
                  className="flex center"
                  style={{ width: "100%" }}
                >
                  {t(enrollment["is_active"] ? "Turn off" : "Turn on")}
                </ButtonComponent>
              </EnrollmentCard>
            ))}
          </Flex>
          <>
            {!isMobile ? (
              <ButtonComponent
                type="primary"
                size="large"
                onClick={handleSubmit}
                className={"flex center"}
                style={{ width: "100%" }}
              >
                {t("Continue")}
              </ButtonComponent>
            ) : (
              <div className="pp-sticky-cta">
                <MobileCtaButtons
                  isMobile={isMobile}
                  onSaveForLater={handleNavigate}
                  onBack={handleBack}
                />
                <ButtonComponent
                  type="primary"
                  size="large"
                  onClick={handleSubmit}
                  className={"flex center"}
                  style={{ width: "100%" }}
                >
                  {t("Continue")}
                </ButtonComponent>
              </div>
            )}
          </>
        </Flex>
      </Spin>

      <Modal
        classNames={{ wrapper: "video-modal", content: "clear-bg" }}
        {...(isMobile ? { transitionName: "" } : null)}
        open={visible}
        centered={!isMobile}
        closeIcon={<Icon type="close_modal" />}
        footer={null}
        closable="true"
        onCancel={() => setVisible(false)}
        destroyOnClose
        getContainer={() => document.getElementById("main-layout")}
      >
        <ReactPlayer
          controls={true}
          width={"100%"}
          height={"100%"}
          playing={true}
          url={"https://www.youtube.com/watch?v=sH9CslJpDeA"}
        />
      </Modal>
    </Layout>
  )
}

export default OnboardingEnrollmentPage
